import { esportaXLSX } from "../utils/esportaXLSX";
// src/pages/RegistroRichiesteCommPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2, Printer, Edit2, X, Search } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { dominioSito } from "../utils/fornitore";
import { numIt } from "../utils/economiaCommessa";
import SopralluoghiDellaRC from "../components/SopralluoghiDellaRC";

const STATI_RC = {
  emessa:        { label: "Emessa",        bg: "#dbeafe", col: "#1d4ed8" },
  in_lavorazione:{ label: "In lavorazione",bg: "#fef3c7", col: "#92400e" },
  annullata:     { label: "Annullata",     bg: "#f3f4f6", col: "#9ca3af" },
  bozza:           { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  inviata:         { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  presa_in_carico: { label: "In lavorazione", bg: "#fef3c7", col: "#92400e" },
  in_elaborazione: { label: "In lavorazione", bg: "#fef3c7", col: "#92400e" },
  offerta_emessa:  { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  accettata:       { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  rifiutata:       { label: "Annullata",  bg: "#f3f4f6", col: "#9ca3af" },
};

function fmt(n) {
  const v = parseFloat(n)||0;
  return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Conta i giorni lavorativi (lun-ven) trascorsi da una data (YYYY-MM-DD) ad oggi
function giorniLavorativiTrascorsi(dataStr) {
  if (!dataStr) return 0;
  const start = new Date(dataStr+"T00:00:00");
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  if (isNaN(start) || start >= oggi) return 0;
  let count = 0;
  const d = new Date(start);
  while (d < oggi) {
    d.setDate(d.getDate()+1);
    const giorno = d.getDay();
    if (giorno !== 0 && giorno !== 6) count++;
  }
  return count;
}

function calcolaVoce(v) {
  const totFornitori = (v.fornitori||[]).reduce((s,f)=>s+numIt(f.importo),0);
  const totTrasporto = (parseFloat(v.km)||0)*(parseFloat(v.nViaggi)||0)*(parseFloat(v.costoKm)||0)+(parseFloat(v.autostrada)||0);
  const totManodopera = (parseFloat(v.qtaOre)||0)*(parseFloat(v.costoOra)||0);
  const totDiretto = totFornitori + totTrasporto + totManodopera;
  const totCG = totDiretto * (parseFloat(v.percCostiGenerali)||0) / 100;
  const totCosti = totDiretto + totCG;
  const valoreOfferta = totCosti * (1 + (parseFloat(v.percMarkup)||0)/100);
  return { totFornitori, totTrasporto, totManodopera, totDiretto, totCG, totCosti, valoreOfferta };
}

function VoceRow({ voce, idx, onChange, onDelete, nomeAzienda }) {
  const upd = (k, v) => onChange({ ...voce, [k]: v });
  const updFornitore = (i, k, v) => {
    const f = [...(voce.fornitori||[])];
    f[i] = { ...f[i], [k]: v };
    upd("fornitori", f);
  };
  const addFornitore = () => upd("fornitori", [...(voce.fornitori||[]), { nome: "", descrizione: "", importo: "" }]);
  const removeFornitore = i => upd("fornitori", (voce.fornitori||[]).filter((_,j)=>j!==i));

  const totFornitori = (voce.fornitori||[]).reduce((s,f)=>s+numIt(f.importo),0);
  const kmAR = parseFloat(voce.km)||0;
  const nViaggi = parseFloat(voce.nViaggi)||0;
  const costoKm = parseFloat(voce.costoKm)||0;
  const autostrada = parseFloat(voce.autostrada)||0;
  const totTrasporto = kmAR * nViaggi * costoKm + autostrada;
  const qta = parseFloat(voce.qtaOre)||0;
  const costoOra = parseFloat(voce.costoOra)||0;
  const totManodopera = qta * costoOra;
  const totDiretto = totFornitori + totTrasporto + totManodopera;
  const percCG = parseFloat(voce.percCostiGenerali)||0;
  const totCG = totDiretto * percCG / 100;
  const percMarkup = parseFloat(voce.percMarkup)||0;
  const totCosti = totDiretto + totCG;
  const valoreOfferta = totCosti * (1 + percMarkup/100);

  const cellStyle = { padding: "4px 6px", borderBottom: "1px solid #f3f4f6", fontSize: 12, verticalAlign: "top" };
  const inp = { width: "100%", padding: "3px 5px", border: "1px solid #e5e7eb", borderRadius: 5, fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" };
  const num = { ...inp, textAlign: "right" };

  return (
    <tr style={{ background: idx%2===0?"#fff":"#fafafa" }}>
      <td style={{ ...cellStyle, textAlign:"center", color:"#9ca3af", fontWeight:700, width:32 }}>{idx+1}</td>
      <td style={{ ...cellStyle, minWidth:160 }}>
        <input style={inp} value={voce.descrizione||""} onChange={e=>upd("descrizione",e.target.value)} placeholder="Descrizione voce"/>
      </td>
      <td style={{ ...cellStyle, width:100 }}>
        <input style={inp} value={voce.spesaCarico||nomeAzienda||"Azienda"} onChange={e=>upd("spesaCarico",e.target.value)}/>
      </td>
      <td style={{ ...cellStyle, minWidth:220 }}>
        {(voce.fornitori||[]).map((f,i)=>(
          <div key={i} style={{ display:"flex", gap:4, marginBottom:3 }}>
            <input style={{...inp,flex:1.3}} value={f.nome||""} onChange={e=>updFornitore(i,"nome",e.target.value)} placeholder="Fornitore"/>
            <input style={{...inp,flex:1.5}} value={f.descrizione||""} onChange={e=>updFornitore(i,"descrizione",e.target.value)} placeholder="Descrizione materiale"/>
            <input style={{...num,flex:1,width:70}} type="number" value={f.importo||""} onChange={e=>updFornitore(i,"importo",e.target.value)} placeholder="€"/>
            <button onClick={()=>removeFornitore(i)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",flexShrink:0}}>×</button>
          </div>
        ))}
        <button onClick={addFornitore} style={{fontSize:10,color:"#7c3aed",background:"none",border:"none",cursor:"pointer"}}>+ Fornitore</button>
        {totFornitori>0 && <div style={{fontSize:11,fontWeight:700,color:"#374151",marginTop:2}}>Tot: €{fmt(totFornitori)}</div>}
      </td>
      <td style={{ ...cellStyle, minWidth:160 }}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3,marginBottom:3}}>
          <div><div style={{fontSize:9,color:"#9ca3af"}}>Km A/R</div><input style={num} type="number" value={voce.km||""} onChange={e=>upd("km",e.target.value)}/></div>
          <div><div style={{fontSize:9,color:"#9ca3af"}}>N° viaggi</div><input style={num} type="number" value={voce.nViaggi||""} onChange={e=>upd("nViaggi",e.target.value)}/></div>
          <div><div style={{fontSize:9,color:"#9ca3af"}}>€/km</div><input style={num} type="number" step="0.01" value={voce.costoKm||""} onChange={e=>upd("costoKm",e.target.value)}/></div>
          <div><div style={{fontSize:9,color:"#9ca3af"}}>Autostrada €</div><input style={num} type="number" value={voce.autostrada||""} onChange={e=>upd("autostrada",e.target.value)}/></div>
        </div>
        {totTrasporto>0 && <div style={{fontSize:11,fontWeight:700,color:"#374151"}}>Tot: €{fmt(totTrasporto)}</div>}
      </td>
      <td style={{ ...cellStyle, minWidth:130 }}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3,marginBottom:3}}>
          <div><div style={{fontSize:9,color:"#9ca3af"}}>Ore</div><input style={num} type="number" value={voce.qtaOre||""} onChange={e=>upd("qtaOre",e.target.value)}/></div>
          <div><div style={{fontSize:9,color:"#9ca3af"}}>€/ora</div><input style={num} type="number" value={voce.costoOra||""} onChange={e=>upd("costoOra",e.target.value)}/></div>
        </div>
        {totManodopera>0 && <div style={{fontSize:11,fontWeight:700,color:"#374151"}}>Tot: €{fmt(totManodopera)}</div>}
      </td>
      <td style={{ ...cellStyle, width:80, textAlign:"right" }}>
        <input style={num} type="number" step="0.1" value={voce.percCostiGenerali||""} onChange={e=>upd("percCostiGenerali",e.target.value)} placeholder="%"/>
        {totCG>0 && <div style={{fontSize:10,color:"#6b7280"}}>€{fmt(totCG)}</div>}
      </td>
      <td style={{ ...cellStyle, width:70, textAlign:"right" }}>
        <input style={num} type="number" step="0.1" value={voce.percMarkup||""} onChange={e=>upd("percMarkup",e.target.value)} placeholder="%"/>
      </td>
      <td style={{ ...cellStyle, width:100, textAlign:"right", fontWeight:700, color:"#059669" }}>
        €{fmt(valoreOfferta)}
      </td>
      <td style={{ ...cellStyle, minWidth:100 }}>
        <input style={inp} value={voce.note||""} onChange={e=>upd("note",e.target.value)} placeholder="Note..."/>
      </td>
      <td style={{ ...cellStyle, width:32, textAlign:"center" }}>
        <button onClick={onDelete} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}>
          <Trash2 size={13}/>
        </button>
      </td>
    </tr>
  );
}

export function RCFormAutonomo({ rc, orgId, orgInfo, commesse, clienti, onClose, nascondiCommessa, soloLettura }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [form, setForm] = useState(rc || {
    oggetto: "", sito: "", dataEmissione: toLocalDateStr(new Date()),
    stato: "bozza", clienteId: "", clienteNome: "", projectId: "", projectName: "", note: "",
    assegnatarioUid: "", assegnatarioNome: "",
  });
  const [voci, setVoci] = useState(rc?.voci || []);
  const [saving, setSaving] = useState(false);
  const [utenti, setUtenti] = useState([]);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  // Carica utenti org per assegnazione
  useEffect(()=>{
    if(!orgId) return;
    getDocs(query(collection(db,"users"),where("organizationId","==",orgId)))
      .then(snap=>setUtenti(snap.docs.map(d=>({uid:d.id,...d.data()})).filter(u=>u.enabled!==false)));
  },[orgId]);

  // Default aziendali per nuova RC
  const rcPresente = !!rc;
  useEffect(()=>{
    if(rcPresente||!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap=>{
      if(!snap.exists()) return;
      const d = snap.data();
      setVoci([{
        descrizione:"", spesaCarico: d.nome||d.name||"Azienda",
        fornitori:[{nome:"",importo:""}],
        km:"", nViaggi:"1", costoKm: d.costoKmDefault||0.30, autostrada:"",
        qtaOre:"", costoOra: d.costoOrarioBase||"",
        percCostiGenerali: d.costiGeneraliDefault ?? 10,
        percMarkup: d.markupDefault ?? 15, note:""
      }]);
    });
  },[orgId, rcPresente]);

  const addVoce = () => {
    const ultima = voci[voci.length-1];
    setVoci(v=>[...v, {
      descrizione:"", spesaCarico: orgInfo?.nome||orgInfo?.name||orgInfo?.name||"Azienda",
      fornitori:[{nome:"",importo:""}],
      km:"", nViaggi:"1", costoKm: ultima?.costoKm||0.30, autostrada:"",
      qtaOre:"", costoOra:"",
      percCostiGenerali: ultima?.percCostiGenerali ?? 15,
      percMarkup: ultima?.percMarkup ?? 10, note:""
    }]);
  };

  const calcolaTotali = () => {
    let totOff=0, totCosti=0;
    voci.forEach(v=>{
      const c = calcolaVoce(v);
      totCosti += c.totCosti;
      totOff += c.valoreOfferta;
    });
    return {totCosti,totOff};
  };

  const salva = async () => {
    if(!form.oggetto.trim()){addToast("Inserisci l'oggetto","error");return;}
    setSaving(true);
    const {totCosti,totOff} = calcolaTotali();
    const commessa = commesse.find(c=>c.id===form.projectId);
    const dati = {
      ...form,
      projectName: commessa?.name||form.projectName||"",
      codiceCommessa: commessa?.codiceCommessa||"",
      voci, orgId,
      orgNome: orgInfo?.nome||orgInfo?.name||orgInfo?.name||"",
      totaleCosti: totCosti, totaleOfferta: totOff,
      autonoma: !form.projectId, // vero solo se non è collegata a nessuna commessa
      updatedAt: serverTimestamp(),
    };
    if(rc?.id){
      await updateDoc(doc(db,"richiesteCommerciali",rc.id),dati);
      if(form.assegnatarioUid && form.assegnatarioUid !== rc.assegnatarioUid){
        await addDoc(collection(db,"notifiche"),{
          userId: form.assegnatarioUid, orgId,
          tipo: "richiesta_commerciale",
          titolo: "Richiesta commerciale assegnata",
          testo: `${currentUser?.displayName||"Il PM"} ti ha assegnato la richiesta ${rc.numero}: "${form.oggetto}"`,
          projectId: form.projectId||null, letta: false,
          createdAt: serverTimestamp(),
        });
      }
      addToast("Richiesta aggiornata ✅","success");
    } else {
      const anno = new Date().getFullYear();
      const prog = await prossimoProgressivo(`${orgId}_rc_${anno}`, { orgId, anno });
      const numero=`RC-${anno}-${String(prog).padStart(4,"0")}`;
      await addDoc(collection(db,"richiesteCommerciali"),{...dati,numero,
        compilatoreUid: currentUser?.uid,
        compilatoreNome: currentUser?.displayName||"",
        createdAt:serverTimestamp()});
      if(form.assegnatarioUid){
        await addDoc(collection(db,"notifiche"),{
          userId: form.assegnatarioUid, orgId,
          tipo: "richiesta_commerciale",
          titolo: "Nuova richiesta commerciale",
          testo: `${currentUser?.displayName||"Il PM"} ti ha assegnato la richiesta ${numero}: "${form.oggetto}"`,
          projectId: form.projectId||null, letta: false,
          createdAt: serverTimestamp(),
        });
      }
      addToast(`Richiesta ${numero} creata ✅`,"success");
    }
    setSaving(false);
    onClose();
  };

  const generaHTML = (perStampa) => {
    const logoHtml = orgInfo?.logo?`<img src="${orgInfo.logo}" style="height:40px;object-fit:contain;"/>`:
      `<div style="font-size:18px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const {totCosti,totOff} = calcolaTotali();

    const vociHtml = voci.map((v,i)=>{
      const c = calcolaVoce(v);

      const fornitoriRows = (v.fornitori||[]).filter(f=>f.nome||f.importo||f.descrizione).map(f=>
        `<div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f3f4f6;">
          <span style="font-size:10px;color:#374151">${f.nome||"—"}${f.descrizione?` <span style="color:#9ca3af">— ${f.descrizione}</span>`:""}</span>
          <span style="font-size:10px;font-weight:700;color:#374151;white-space:nowrap;padding-left:8px">€${fmt(f.importo)}</span>
        </div>`
      ).join("");

      const formulaTrasporto = v.km
        ? `${v.km} km × ${v.nViaggi||1} viaggio/i × €${v.costoKm}/km${v.autostrada?` + €${v.autostrada} autostrada`:""} = <strong>€${fmt(c.totTrasporto)}</strong>`
        : "—";
      const formulaMano = v.qtaOre
        ? `${v.qtaOre} × €${v.costoOra} = <strong>€${fmt(c.totManodopera)}</strong>`
        : "—";

      return `<div style="border:1px solid #e5e7eb;border-radius:10px;margin-bottom:14px;overflow:hidden;page-break-inside:avoid;">
        <div style="background:#1e1b4b;padding:9px 14px;display:flex;justify-content:space-between;align-items:center;">
          <div style="display:flex;align-items:center;gap:10px;">
            <span style="background:#fff;color:#1e1b4b;width:22px;height:22px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;flex-shrink:0">${i+1}</span>
            <span style="color:#fff;font-weight:700;font-size:13px">${v.descrizione||"—"}</span>
          </div>
          <div>
            <span style="color:rgba(255,255,255,.55);font-size:10px;margin-right:12px">Spesa a carico: ${v.spesaCarico||"—"}</span>
            <span style="color:#4ade80;font-weight:900;font-size:16px">€${fmt(c.valoreOfferta)}</span>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1.4fr 0.6fr 1fr;border-bottom:1px solid #e5e7eb;">
          <div style="padding:12px 14px;border-right:1px solid #e5e7eb;">
            <div style="font-size:9px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">📦 Costi Fornitori e Prestazioni</div>
            ${fornitoriRows || `<div style="font-size:10px;color:#d1d5db">Nessun fornitore</div>`}
            ${c.totFornitori>0?`<div style="font-size:11px;font-weight:800;color:#374151;margin-top:6px;padding-top:4px;border-top:1px solid #e5e7eb;text-align:right">Totale fornitori: <span style="color:#7c3aed">€${fmt(c.totFornitori)}</span></div>`:""}
          </div>
          <div style="padding:12px 14px;border-right:1px solid #e5e7eb;background:#f0f9ff;">
            <div style="font-size:9px;color:#0369a1;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">🚗 Trasporto e viaggio</div>
            ${v.km?`
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;">
              <div><div style="font-size:8px;color:#9ca3af">Km A/R</div><div style="font-size:11px;font-weight:600">${v.km}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">N° viaggi</div><div style="font-size:11px;font-weight:600">${v.nViaggi||1}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">€/km</div><div style="font-size:11px;font-weight:600">€${v.costoKm}</div></div>
              ${v.autostrada?`<div><div style="font-size:8px;color:#9ca3af">Autostrada</div><div style="font-size:11px;font-weight:600">€${v.autostrada}</div></div>`:""}
            </div>
            <div style="font-size:10px;color:#0369a1;background:#dbeafe;padding:4px 8px;border-radius:5px">${formulaTrasporto}</div>`
            :`<div style="font-size:10px;color:#d1d5db">Nessun trasporto</div>`}
          </div>
          <div style="padding:12px 14px;background:#f0fdf4;">
            <div style="font-size:9px;color:#166534;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">👷 Manodopera</div>
            ${v.qtaOre?`
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin-bottom:6px;">
              <div><div style="font-size:8px;color:#9ca3af">Quantità</div><div style="font-size:11px;font-weight:600">${v.qtaOre}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">Costo unit.</div><div style="font-size:11px;font-weight:600">€${v.costoOra}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">Totale</div><div style="font-size:13px;font-weight:800;color:#166534">€${fmt(c.totManodopera)}</div></div>
            </div>
            <div style="font-size:10px;color:#166534;background:#dcfce7;padding:4px 8px;border-radius:5px">${formulaMano}</div>`
            :`<div style="font-size:10px;color:#d1d5db">Nessuna manodopera</div>`}
          </div>
        </div>
        <div style="background:#fafafa;padding:10px 14px;">
          <div style="font-size:9px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">📊 Riepilogo economico voce</div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr 1fr;gap:8px;align-items:center;">
            <div style="background:#fff;border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#9ca3af">Costo diretto</div>
              <div style="font-size:13px;font-weight:700;color:#374151">€${fmt(c.totDiretto)}</div>
            </div>
            <div style="background:#fff;border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#9ca3af">Costi generali</div>
              <div style="font-size:13px;font-weight:700;color:#374151">${v.percCostiGenerali||0}%</div>
            </div>
            <div style="background:#fff;border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#9ca3af">Importo C.G.</div>
              <div style="font-size:13px;font-weight:700;color:#374151">€${fmt(c.totCG)}</div>
            </div>
            <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#92400e">Markup (${v.percMarkup||0}%)</div>
              <div style="font-size:13px;font-weight:700;color:#d97706">+€${fmt(c.valoreOfferta-c.totCosti)}</div>
            </div>
            <div style="background:#d1fae5;border:1px solid #86efac;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#065f46;font-weight:700">Valore offerta</div>
              <div style="font-size:16px;font-weight:900;color:#059669">€${fmt(c.valoreOfferta)}</div>
            </div>
          </div>
        </div>
        ${v.note?`<div style="padding:7px 14px;background:#fef9f0;font-size:10px;color:#6b7280;border-top:1px solid #e5e7eb">📝 Note: ${v.note}</div>`:""}
      </div>`;
    }).join("");

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>RC ${rc?.numero||"BOZZA"} — ${form.oggetto}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:12mm 16mm;}
      @media screen{body{max-width:1050px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:flex-start;}
      .info{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 12px;margin-bottom:12px;}
      .lbl{font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;display:block;margin-bottom:2px;}
      .val{font-size:11px;font-weight:600;}
      .footer{border-top:1px solid #e5e7eb;margin-top:12px;padding-top:6px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>${logoHtml}
        <div style="font-weight:700;margin-top:3px">${orgInfo?.nome||orgInfo?.name||""}</div>
        ${orgInfo?.indirizzo?`<div style="font-size:9px;color:#6b7280">${orgInfo.indirizzo}</div>`:""}
        ${orgInfo?.piva?`<div style="font-size:9px;color:#6b7280">P.IVA ${orgInfo.piva}</div>`:""}
      </div>
      <div style="text-align:right">
        <div style="font-size:14px;font-weight:800;color:#1e1b4b">RICHIESTA COMMERCIALE</div>
        <div style="font-size:16px;font-weight:900;color:#d97706;font-family:monospace">${rc?.numero||"BOZZA"}</div>
        <span style="padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:${STATI_RC[form.stato]?.bg};color:${STATI_RC[form.stato]?.col}">${STATI_RC[form.stato]?.label||form.stato}</span>
      </div>
    </div>
    <div class="info" style="grid-template-columns:repeat(5,1fr);">
      <div><span class="lbl">Commessa</span><span class="val">${form.projectName?`${(commesse.find(c=>c.id===form.projectId)?.codiceCommessa)?`[${commesse.find(c=>c.id===form.projectId)?.codiceCommessa}] `:""}${form.projectName}`:"— Nessuna —"}</span></div>
      <div><span class="lbl">Cliente</span><span class="val">${form.clienteNome||"—"}</span></div>
      <div><span class="lbl">Sito</span><span class="val">${form.sito||"—"}</span></div>
      <div><span class="lbl">Data emissione</span><span class="val">${form.dataEmissione||"—"}</span></div>
      <div><span class="lbl">Assegnato a</span><span class="val">${form.assegnatarioNome||"—"}</span></div>
    </div>
    <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:7px 12px;margin-bottom:10px;font-weight:600;color:#92400e">${form.oggetto}</div>
    ${vociHtml}
    <div style="background:#f0fdf4;border:2px solid #86efac;border-radius:8px;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
      <div style="font-size:12px;font-weight:700;color:#374151">TOTALE COSTI: <span style="color:#374151">€${fmt(totCosti)}</span></div>
      <div style="font-size:16px;font-weight:900;color:#059669">TOTALE OFFERTA: €${fmt(totOff)}</div>
    </div>
    ${form.note?`<div style="margin-top:10px;padding:8px 12px;background:#fef3c7;border-radius:6px">${form.note}</div>`:""}
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Compilato da: ${rc?.compilatoreNome||currentUser?.displayName||""}</span>
      <span>${perStampa?"Stampato":"Consultato"} il ${new Date().toLocaleDateString("it-IT")}</span>
    </div>
    ${perStampa?``:""}
    </body></html>`;
  };

  const stampaPDF = () => {
    const win = window.open("","_blank");
    win.document.write(generaHTML(true));
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  const {totCosti,totOff} = calcolaTotali();
  const inp2 = {padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"};

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,gap:8}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#1e1b4b"}}>{soloLettura?`Consultazione ${rc?.numero||""}`:(rc?`Modifica ${rc.numero}`:"Nuova richiesta commerciale")}</div>
          <div style={{fontSize:11,color:soloLettura?"#6b7280":"#f59e0b",fontWeight:600,marginTop:2}}>{soloLettura?"👁️ Sola consultazione — per modificarla, vai dalla commessa di origine o da Personale":"💼 Documento autonomo — non collegato a una commessa specifica"}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {rc && <button onClick={stampaPDF} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 14px",border:"1px solid #2563eb",borderRadius:8,background:"#dbeafe",color:"#1d4ed8",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}><Printer size={13}/> PDF</button>}
          {!soloLettura && <button onClick={salva} disabled={saving} style={{padding:"6px 16px",border:"none",borderRadius:8,background:"#d97706",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            {saving?"...":"Salva"}
          </button>}
          <button onClick={onClose} style={{padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer"}}><X size={14}/></button>
        </div>
      </div>

      {/* sopralluoghi nati da questa richiesta (W416) */}
      {rc?.id && <div style={{padding:"0 20px",flexShrink:0}}><SopralluoghiDellaRC rc={rc} orgId={orgId}/></div>}

      {soloLettura ? (
        <iframe title="Anteprima richiesta commerciale" srcDoc={generaHTML(false)}
          style={{flex:1,border:"none",width:"100%"}}/>
      ) : (
      <fieldset disabled={!!soloLettura} style={{border:"none",padding:0,margin:0,display:"flex",flexDirection:"column",flex:1,overflow:"hidden",minHeight:0}}>

      {(orgInfo?.costiGeneraliDefault===undefined || orgInfo?.markupDefault===undefined) && (
        <div style={{background:"#fee2e2",borderBottom:"1px solid #fca5a5",padding:"7px 20px",flexShrink:0,fontSize:11,fontWeight:600,color:"#991b1b"}}>
          ⚠️ Questa organizzazione non ha ancora personalizzato i parametri commerciali in Impostazioni Aziendali — si stanno usando i valori di default (Margine/Utile 15%, Costi generali 10%). Verifica se vanno modificati.
        </div>
      )}

      {/* Campi testata */}
      <div style={{background:"#fffbeb",borderBottom:"1px solid #fde68a",padding:"10px 20px",display:"grid",gridTemplateColumns: nascondiCommessa ? "1.6fr 1fr 1fr 1fr 1fr" : "1.6fr 1fr 1fr 1fr 1fr 1fr",gap:10,flexShrink:0}}>
        <div>
          <label style={{fontSize:10,color:"#92400e",fontWeight:600,display:"block",marginBottom:3}}>OGGETTO *</label>
          <input style={{...inp2,width:"100%"}} value={form.oggetto} onChange={e=>upd("oggetto",e.target.value)} placeholder="Es. Fornitura pompa antincendio"/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#92400e",fontWeight:600,display:"block",marginBottom:3}}>SITO</label>
          <input style={{...inp2,width:"100%"}} value={form.sito||""} onChange={e=>upd("sito",e.target.value)} placeholder="Nome sito / cantiere"/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#92400e",fontWeight:600,display:"block",marginBottom:3}}>CLIENTE</label>
          <select style={{...inp2,width:"100%"}} value={form.clienteId||""} onChange={e=>{
            const cl = clienti.find(c=>c.id===e.target.value);
            upd("clienteId",e.target.value);
            upd("clienteNome",cl?.ragioneSociale||"");
          }}>
            <option value="">— Seleziona cliente —</option>
            {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
          </select>
          {!form.clienteId && <input style={{...inp2,width:"100%",marginTop:4,fontSize:12}} value={form.clienteNome||""} onChange={e=>upd("clienteNome",e.target.value)} placeholder="O scrivi nome cliente/potenziale cliente..."/>}
        </div>
        {!nascondiCommessa && (
          <div>
            <label style={{fontSize:10,color:"#92400e",fontWeight:600,display:"block",marginBottom:3}}>COLLEGA COMMESSA (opz.)</label>
            <select style={{...inp2,width:"100%"}} value={form.projectId||""} onChange={e=>{
              const c=commesse.find(x=>x.id===e.target.value);
              upd("projectId",e.target.value);
              upd("projectName",c?.name||"");
            }}>
              <option value="">— Nessuna commessa —</option>
              {commesse.map(c=><option key={c.id} value={c.id}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={{fontSize:10,color:"#92400e",fontWeight:600,display:"block",marginBottom:3}}>DATA · STATO</label>
          <div style={{display:"flex",gap:4}}>
            <input type="date" style={{...inp2,flex:1,fontSize:12}} value={form.dataEmissione||""} onChange={e=>upd("dataEmissione",e.target.value)}/>
            <select style={{...inp2,fontSize:11}} value={form.stato} onChange={e=>upd("stato",e.target.value)}>
              {Object.entries(STATI_RC).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={{fontSize:10,color:"#d97706",fontWeight:700,display:"block",marginBottom:3}}>📤 ASSEGNA AL COMMERCIALE</label>
          <select style={{...inp2,width:"100%",borderColor:form.assegnatarioUid?"#d97706":"#e5e7eb"}} value={form.assegnatarioUid||""} onChange={e=>{
            const u=utenti.find(x=>x.uid===e.target.value);
            upd("assegnatarioUid",e.target.value);
            upd("assegnatarioNome",u?.displayName||"");
          }}>
            <option value="">— Nessuno —</option>
            {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
          </select>
        </div>
      </div>

      {/* Tabella voci */}
      <div style={{flex:1,overflowY:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead style={{position:"sticky",top:0,zIndex:5}}>
            <tr style={{background:"#1e1b4b"}}>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:32,textAlign:"center"}}>#</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,textAlign:"left"}}>Descrizione</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:100}}>Spesa a carico</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:180}}>Fornitori</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:160}}>Trasporto</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:130}}>Manodopera</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:80,textAlign:"right"}}>Costi gen. %</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:70,textAlign:"right"}}>Markup %</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:100,textAlign:"right"}}>Valore offerta</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:100}}>Note</th>
              <th style={{padding:"7px 8px",color:"#fff",fontSize:10,fontWeight:700,width:32}}></th>
            </tr>
          </thead>
          <tbody>
            {voci.map((v,i)=>(
              <VoceRow key={i} voce={v} idx={i} nomeAzienda={orgInfo?.nome||orgInfo?.name||orgInfo?.name}
                onChange={nv=>setVoci(vs=>vs.map((x,j)=>j===i?nv:x))}
                onDelete={()=>setVoci(vs=>vs.filter((_,j)=>j!==i))}/>
            ))}
          </tbody>
        </table>
        <div style={{padding:"10px 16px"}}>
          <button onClick={addVoce} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",border:"1px dashed #d97706",borderRadius:8,background:"#fffbeb",color:"#d97706",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <Plus size={13}/> Aggiungi voce
          </button>
        </div>
      </div>

      {/* Footer totali */}
      <div style={{borderTop:"2px solid #e5e7eb",background:"#fff",padding:"10px 20px",display:"flex",alignItems:"center",justifyContent:"flex-end",gap:16,flexShrink:0}}>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,textTransform:"uppercase"}}>Totale costi</div>
          <div style={{fontSize:16,fontWeight:800,color:"#374151"}}>€{fmt(totCosti)}</div>
        </div>
        <div style={{width:1,height:40,background:"#e5e7eb"}}/>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:10,color:"#059669",fontWeight:700,textTransform:"uppercase"}}>Valore offerta totale</div>
          <div style={{fontSize:22,fontWeight:900,color:"#059669"}}>€{fmt(totOff)}</div>
        </div>
        <div style={{marginLeft:16}}>
          <label style={{fontSize:10,color:"#9ca3af",fontWeight:600,display:"block",marginBottom:2}}>Note generali</label>
          <input value={form.note||""} onChange={e=>upd("note",e.target.value)} placeholder="Note..."
            style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",width:220}}/>
        </div>
      </div>
      </fieldset>
      )}
    </div>
  );
}

// Pagina principale registro
export default function RegistroRichiesteCommPage({ onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [richieste, setRichieste] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [editRC, setEditRC] = useState(null);
  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [soloMie, setSoloMie] = useState(false);
  const [soloCreateMe, setSoloCreateMe] = useState(false);
  const [soloRitardo, setSoloRitardo] = useState(false);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db,"richiesteCommerciali"),where("orgId","==",effectiveOrgId)),
      snap=>setRichieste(snap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)))
    );
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(snap=>{if(snap.exists())setOrgInfo(snap.data());});
    onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId),where("archived","==",false)),
      snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.isParent&&!p.senzaCliente&&p.statoCommessa!=="chiusa")));
    onSnapshot(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)),
      snap=>setClienti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  },[effectiveOrgId]);

  const elimina = async id=>{
    if(!await confirm("Eliminare questa richiesta commerciale?",{type:"danger"})) return;
    await deleteDoc(doc(db,"richiesteCommerciali",id));
    addToast("Eliminata","info");
  };

  const filtrate = richieste.filter(r=>{
    if(soloMie && r.assegnatarioUid!==currentUser?.uid) return false;
    if(soloCreateMe && r.compilatoreUid!==currentUser?.uid) return false;
    if(soloRitardo && (r.stato==="annullata" || giorniLavorativiTrascorsi(r.dataEmissione)<=5)) return false;
    if(filtroStato && r.stato!==filtroStato) return false;
    if(cerca){
      const s=cerca.toLowerCase();
      return (r.numero||"").toLowerCase().includes(s)||
        (r.oggetto||"").toLowerCase().includes(s)||
        (r.clienteNome||"").toLowerCase().includes(s)||
        (r.projectName||"").toLowerCase().includes(s)||
        (r.sito||"").toLowerCase().includes(s);
    }
    return true;
  });

  if(editRC){
    return <RCFormAutonomo rc={editRC} orgId={effectiveOrgId} orgInfo={orgInfo}
      commesse={commesse} clienti={clienti} soloLettura={true}
      onClose={()=>{setEditRC(null);}}/>;
  }

  const inp = {padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"};

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"16px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>
                💼 Registro Richieste Commerciali
              </h2>
            </div>
            <button onClick={()=>{
              const intestazione=["Numero","Data","Commessa","Oggetto","Assegnatario","Stato","Valore €"];
              const righe=(richieste||[]).map(r=>[ r.numero||"—",r.dataEmissione||"—",r.projectName||"—",r.oggetto||"—",r.assegnatarioNome||"—",r.stato||"—",r.valoreOfferta?Number(r.valoreOfferta).toFixed(2):"0.00"]);
              esportaXLSX([intestazione,...righe],`Registro_Inoltri_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Inoltri");
            }} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              📊 Excel
            </button>
            <p style={{fontSize:12,color:"#9ca3af",margin:"2px 0 0"}}>{filtrate.length} richiesta/e · Tutte le organizzazione · 👁️ Sola consultazione — le modifiche vanno fatte dalla commessa di origine, o da Personale per quelle senza commessa</p>
          </div>
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <Search size={13} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
            <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca numero, oggetto, cliente..."
              style={{...inp,paddingLeft:26,width:240}}/>
          </div>
          <button onClick={()=>setSoloMie(v=>!v)}
            style={{padding:"6px 12px",border:"1px solid "+(soloMie?"#d97706":"#e5e7eb"),borderRadius:8,
              background:soloMie?"#fef3c7":"#fff",color:soloMie?"#92400e":"#374151",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            📤 Assegnate a me
          </button>
          <button onClick={()=>setSoloCreateMe(v=>!v)}
            style={{padding:"6px 12px",border:"1px solid "+(soloCreateMe?"#7c3aed":"#e5e7eb"),borderRadius:8,
              background:soloCreateMe?"#f5f3ff":"#fff",color:soloCreateMe?"#5b21b6":"#374151",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            ✍️ Create da me
          </button>
          <button onClick={()=>setSoloRitardo(v=>!v)}
            style={{padding:"6px 12px",border:"1px solid "+(soloRitardo?"#991b1b":"#e5e7eb"),borderRadius:8,
              background:soloRitardo?"#fee2e2":"#fff",color:soloRitardo?"#991b1b":"#374151",fontSize:12,fontWeight:600,cursor:"pointer"}}
            title="Aperte da più di 5 giorni lavorativi senza risposta">
            ⏱️ In ritardo (&gt;5gg)
          </button>
          <select value={filtroStato} onChange={e=>{ setFiltroStato(e.target.value); if(!e.target.value) setSoloRitardo(false); }} style={inp}>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI_RC).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          {(cerca||filtroStato||soloMie||soloCreateMe||soloRitardo) && (
            <button onClick={()=>{setCerca("");setFiltroStato("");setSoloMie(false);setSoloCreateMe(false);setSoloRitardo(false);}}
              style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>
              × Reset
            </button>
          )}
        </div>
      </div>

      <div style={{flex:1,overflow:"auto"}}>
        {filtrate.length===0 ? (
          <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
            <div style={{fontSize:48,marginBottom:12,opacity:.15}}>💼</div>
            <div style={{fontSize:14,fontWeight:600}}>Nessuna richiesta commerciale</div>
            <div style={{fontSize:12,marginTop:4}}>Crea la prima richiesta commerciale</div>
          </div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead style={{position:"sticky",top:0,zIndex:5}}>
              <tr style={{background:"#1e1b4b"}}>
                {["N°","Data","Oggetto","Cliente","Commessa","Stato","Offerta","Compilato da",""].map(h=>(
                  <th key={h} style={{padding:"9px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrate.map((r,i)=>{
                const stato=STATI_RC[r.stato]||STATI_RC.bozza;
                return (
                  <tr key={r.id} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer"}}
                    onClick={()=>setEditRC(r)}>
                    <td style={{padding:"9px 12px",fontFamily:"monospace",fontWeight:800,color:"#d97706"}}>{r.numero||"—"}</td>
                    <td style={{padding:"9px 12px",color:"#6b7280",whiteSpace:"nowrap"}}>{r.dataEmissione||"—"}</td>
                    <td style={{padding:"9px 12px",fontWeight:600,color:"#111827",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.oggetto}</td>
                    <td style={{padding:"9px 12px",color:"#374151"}}>{r.clienteNome||"—"}</td>
                    <td style={{padding:"9px 12px",color:"#7c3aed",fontFamily:"monospace",fontSize:11}}>
                      {r.codiceCommessa?`[${r.codiceCommessa}] `:""}{r.projectName||<span style={{color:"#d1d5db"}}>—</span>}
                    </td>
                    <td style={{padding:"9px 12px"}}>
                      <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700}}>{stato.label}</span>
                    </td>
                    <td style={{padding:"9px 12px",fontWeight:800,color:"#059669"}}>€{(r.totaleOfferta||0).toLocaleString("it-IT",{minimumFractionDigits:2})}</td>
                    <td style={{padding:"9px 12px",color:"#6b7280"}}>{r.compilatoreNome||"—"}</td>
                    <td style={{padding:"9px 12px"}} onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>elimina(r.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}>
                        <Trash2 size={13}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
